import { useEffect, useMemo, useState } from 'react'
import { api } from '../api/client'
import type { AdminUserRow } from '../api/types'
import { useAsync } from '../lib/useAsync'
import { number, shortDate } from '../lib/format'
import { Avatar, EmptyState, ErrorState, PageHeader, TableSkeleton } from '../components/ui'
import './Users.css'

/**
 * Admin · İstifadəçilər.
 *
 * The panel had no way to see who is actually using the product — moderators
 * were listed, members were not — so the only answer to "how many people are
 * there" was a psql session. This is that list.
 *
 * Read-only on purpose. Blocking, warning and deleting all happen through the
 * report queue, where the decision is recorded in the journal against the
 * report that prompted it. An action taken from a list of names would be an
 * action with no reason attached to it.
 */

const PAGE = 50

export function Users() {
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const [page, setPage] = useState(0)

  // The list is searched by typing; one request per keystroke would be one
  // per keystroke too many.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounced(query.trim())
      setPage(0)
    }, 300)
    return () => clearTimeout(timer)
  }, [query])

  const { data, error, loading, reload } = useAsync(
    () => api.users({ q: debounced || undefined, limit: PAGE, offset: page * PAGE }),
    [debounced, page],
  )

  const total = data?.total ?? 0
  const rows = data?.items ?? []
  const pages = Math.max(1, Math.ceil(total / PAGE))

  const subtitle = useMemo(() => {
    if (loading) return 'Yüklənir…'
    if (debounced) return `“${debounced}” üçün ${number(total)} nəticə`
    return `${number(total)} hesab · ən yenisi əvvəldə`
  }, [loading, debounced, total])

  return (
    <>
      <PageHeader title="İstifadəçilər" subtitle={subtitle} />

      <section className="card users">
        <div className="users-search">
          <input
            type="search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Ad, istifadəçi adı və ya e-poçt — ən azı 2 hərf"
            aria-label="İstifadəçi axtar"
          />
        </div>

        {error ? (
          <ErrorState error={error} onRetry={reload} />
        ) : loading ? (
          <TableSkeleton rows={8} />
        ) : rows.length === 0 ? (
          <EmptyState
            title={debounced ? 'Nəticə tapılmadı' : 'Hələ istifadəçi yoxdur'}
            description={
              debounced
                ? 'Başqa ad və ya e-poçt yaz.'
                : 'İlk qeydiyyatdan sonra siyahı burada görünəcək.'
            }
          />
        ) : (
          <table className="users-table">
            <thead>
              <tr>
                <th>Hesab</th>
                <th>E-poçt</th>
                <th className="num">Ulduz</th>
                <th className="num">Post</th>
                <th className="num">Səs</th>
                <th className="num">Məsləhət</th>
                <th>Qoşulub</th>
                <th>Son aktivlik</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((user) => (
                <tr key={user.id} className={user.deleted ? 'is-deleted' : undefined}>
                  <td>
                    <div className="users-account">
                      <Avatar initials={user.initials} title={user.displayName} />
                      <div>
                        <div className="users-name">
                          {user.displayName}
                          <Tags user={user} />
                        </div>
                        <div className="muted">@{user.username}</div>
                      </div>
                    </div>
                  </td>
                  <td className="users-email">{user.email ?? '—'}</td>
                  <td className="num">{number(user.stars)}</td>
                  <td className="num">{number(user.posts)}</td>
                  <td className="num">{number(user.votes)}</td>
                  <td className="num">{number(user.advices)}</td>
                  <td>{shortDate(user.joinedAt)}</td>
                  <td>{shortDate(user.lastActiveAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {pages > 1 && (
          <div className="users-pager">
            <button type="button" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
              ‹ Əvvəlki
            </button>
            <span className="muted">
              {page + 1} / {pages}
            </span>
            <button
              type="button"
              disabled={page + 1 >= pages}
              onClick={() => setPage((p) => p + 1)}
            >
              Növbəti ›
            </button>
          </div>
        )}
      </section>
    </>
  )
}

/**
 * The states an operator needs to see at a glance.
 *
 * Only the ones that change how a row should be read — a plain account carries
 * no tag at all, so the eye goes to the rows that are not plain.
 */
function Tags({ user }: { user: AdminUserRow }) {
  return (
    <>
      {user.system && <span className="tag tag-system">Rəsmi</span>}
      {user.premium && <span className="tag tag-premium">Premium</span>}
      {user.role !== 'USER' && <span className="tag tag-role">{roleLabel(user.role)}</span>}
      {user.blocked && <span className="tag tag-blocked">Bloklu</span>}
      {user.warnings > 0 && !user.blocked && (
        <span className="tag tag-warn">{user.warnings} xəbərdarlıq</span>
      )}
      {user.deleted && <span className="tag tag-deleted">Silinib</span>}
    </>
  )
}

function roleLabel(role: AdminUserRow['role']): string {
  switch (role) {
    case 'MODERATOR':
      return 'Moderator'
    case 'SENIOR_MODERATOR':
      return 'Baş moderator'
    case 'ADMIN':
      return 'Admin'
    case 'SUPER_ADMIN':
      return 'Baş admin'
    default:
      return ''
  }
}
